import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, PoundSterling, Save } from "lucide-react";
import { toast } from "sonner";

function PlanRow({ p, onChange }) {
  const set = (k) => (e) => onChange({ ...p, [k]: e.target.value });
  return (
    <div className="rounded-2xl border border-border bg-card/40 p-5" data-testid={`admin-plan-${p.lookup_key}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="font-serif text-xl">{p.name || p.lookup_key}</span>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          Visible <Switch checked={p.active} onCheckedChange={(v) => onChange({ ...p, active: v })} data-testid={`admin-plan-active-${p.lookup_key}`} />
        </label>
      </div>
      <div className="grid sm:grid-cols-4 gap-3">
        <div><Label className="text-xs">Name</Label><Input value={p.name} onChange={set("name")} className="mt-1" data-testid={`admin-plan-name-${p.lookup_key}`} /></div>
        <div><Label className="text-xs">Price (£ / {p.interval})</Label>
          <Input type="number" step="0.01" min="1" value={p.pounds} onChange={set("pounds")} className="mt-1" data-testid={`admin-plan-price-${p.lookup_key}`} /></div>
        <div><Label className="text-xs">Badge</Label><Input value={p.badge} onChange={set("badge")} className="mt-1" data-testid={`admin-plan-badge-${p.lookup_key}`} /></div>
        <div><Label className="text-xs">Saving label</Label><Input value={p.save} onChange={set("save")} className="mt-1" data-testid={`admin-plan-save-${p.lookup_key}`} /></div>
        <div className="sm:col-span-4"><Label className="text-xs">Description</Label><Input value={p.blurb} onChange={set("blurb")} className="mt-1" data-testid={`admin-plan-blurb-${p.lookup_key}`} /></div>
      </div>
    </div>
  );
}

export default function AdminPricing() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = (d) => setCfg({ ...d, plans: d.plans.map((p) => ({ ...p, pounds: (p.amount / 100).toFixed(2) })) });
  useEffect(() => { api.get("/admin/pricing").then((r) => load(r.data)).catch(() => {}); }, []);
  if (!cfg) return null;

  const f = cfg.founding, fs = cfg.founding_status;
  const setF = (patch) => setCfg({ ...cfg, founding: { ...f, ...patch } });
  const setPlan = (np) => setCfg({ ...cfg, plans: cfg.plans.map((p) => (p.lookup_key === np.lookup_key ? np : p)) });

  const save = async () => {
    setSaving(true);
    try {
      const plans = cfg.plans.map(({ pounds, ...p }) => ({ ...p, amount: Math.round(parseFloat(pounds) * 100) }));
      const { data } = await api.put("/admin/pricing", { plans, founding: f });
      load(data); toast.success("Pricing updated — live on the pricing page now");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save pricing"); }
    setSaving(false);
  };

  return (
    <div className="mt-14 pt-10 border-t border-border" data-testid="admin-pricing">
      <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Pricing</p>
      <h2 className="font-serif text-3xl sm:text-4xl mb-2 flex flex-wrap items-center gap-3"><PoundSterling className="w-6 h-6 text-primary" /> Plans & Founding Light
        <span data-testid="admin-stripe-badge" className={`text-[11px] font-sans tracking-[0.18em] px-3 py-1 rounded-full border ${cfg.stripe_mode === "live" ? "border-teal-400/50 bg-teal-400/10 text-teal-300" : "border-amber-400/50 bg-amber-400/10 text-amber-300"}`}>
          {cfg.stripe_mode === "live" ? "LIVE PAYMENTS" : "TEST MODE"}
        </span>
      </h2>
      <p className="text-muted-foreground mb-6 max-w-xl">Changes apply instantly to the pricing page and checkout. No Stripe setup needed.
        {cfg.stripe_mode === "live"
          ? <span className="text-teal-300" data-testid="admin-stripe-mode"> Real cards are being charged.</span>
          : <span className="text-amber-400" data-testid="admin-stripe-mode"> No real money moves — test with card 4242 4242 4242 4242. Claim your Stripe account to go live.</span>}</p>

      <div className="rounded-2xl border border-primary/30 bg-primary/[0.05] p-5 mb-4" data-testid="admin-founding">
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="font-serif text-xl">Founding Light offer</span>
            <p className="text-xs text-muted-foreground mt-1" data-testid="admin-founding-status">
              {fs.claimed} claimed{fs.spots_left !== null ? ` · ${fs.spots_left} spots left` : ""} · {fs.available ? "Live" : "Not showing"}
            </p>
          </div>
          <Switch checked={f.enabled} onCheckedChange={(v) => setF({ enabled: v })} data-testid="admin-founding-toggle" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Founder limit (0 = no limit)</Label>
            <Input type="number" min="0" value={f.cap} onChange={(e) => setF({ cap: e.target.value })} className="mt-1" data-testid="admin-founding-cap" /></div>
          <div><Label className="text-xs">Ends on (optional)</Label>
            <Input type="date" value={f.ends_at} onChange={(e) => setF({ ends_at: e.target.value })} className="mt-1" data-testid="admin-founding-ends" /></div>
        </div>
      </div>

      <div className="space-y-4 mb-6">{cfg.plans.map((p) => <PlanRow key={p.lookup_key} p={p} onChange={setPlan} />)}</div>
      <Button onClick={save} disabled={saving} className="rounded-full" data-testid="admin-pricing-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save pricing</>}
      </Button>
    </div>
  );
}
